import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const PASSWORD_RULES = [
  { test: (p: string) => p.length >= 8, error: "Password must be at least 8 characters" },
  { test: (p: string) => /[A-Z]/.test(p), error: "Password must contain at least 1 uppercase letter" },
  { test: (p: string) => /[a-z]/.test(p), error: "Password must contain at least 1 lowercase letter" },
  { test: (p: string) => /[0-9]/.test(p), error: "Password must contain at least 1 number" },
  { test: (p: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p), error: "Password must contain at least 1 special character" },
] as const;

const validatePassword = (password: string): { valid: boolean; error?: string } => {
  for (const rule of PASSWORD_RULES) {
    if (!rule.test(password)) {
      return { valid: false, error: rule.error };
    }
  }
  return { valid: true };
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  return error instanceof Error ? error.message : fallback;
};

export const useAuthHandlers = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  const executeAuthAction = async <T>(
    action: () => Promise<T>,
    successMessage: string,
    errorMessage: string,
    loadingState: 'loading' | 'resending' = 'loading'
  ): Promise<T | undefined> => {
    const setLoading = loadingState === 'loading' ? setIsLoading : setIsResending;
    setLoading(true);
    try {
      const result = await action();
      toast.success(successMessage);
      return result;
    } catch (error) {
      toast.error(getErrorMessage(error, errorMessage));
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  const validatePasswordMatch = (password: string, confirmPassword: string): boolean => {
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }
    
    const validation = validatePassword(password);
    if (!validation.valid) {
      toast.error(validation.error || "Invalid password");
      return false;
    }
    
    return true;
  };

  const handleSignUp = async (email: string, password: string) => {
    await executeAuthAction(
      async () => {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/` },
        });
        if (error) throw error;
        navigate("/");
      },
      "Account created successfully!",
      "Failed to create account"
    );
  };

  const handleSignIn = async (email: string, password: string) => {
    setIsLoading(true);
    setNeedsVerification(false);

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        if (error.message?.includes('Email not confirmed')) {
          setNeedsVerification(true);
          toast.error("Please verify your email address");
        } else {
          throw error;
        }
        return;
      }

      toast.success("Signed in successfully!");
      navigate("/");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to sign in"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async (email: string) => {
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    await executeAuthAction(
      async () => {
        const { error } = await supabase.auth.resend({
          type: 'signup',
          email,
          options: { emailRedirectTo: `${window.location.origin}/` }
        });
        if (error) throw error;
      },
      "Verification email sent! Check your inbox.",
      "Failed to resend verification email",
      'resending'
    );
  };

  const handleForgotPassword = async (email: string) => {
    await executeAuthAction(
      async () => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth`,
        });
        if (error) throw error;
      },
      "Password reset email sent! Check your inbox.",
      "Failed to send password reset email"
    );
  };

  const handleSignUpWithConfirm = async (email: string, password: string, confirmPassword: string) => {
    if (!validatePasswordMatch(password, confirmPassword)) return;
    await handleSignUp(email, password);
  };

  const handleResetPassword = async (newPassword: string, confirmPassword: string): Promise<boolean> => {
    if (!validatePasswordMatch(newPassword, confirmPassword)) return false;

    const result = await executeAuthAction(
      async () => {
        const { error } = await supabase.auth.updateUser({
          password: newPassword
        });
        if (error) throw error;
        navigate("/");
        return true;
      },
      "Password updated successfully!",
      "Failed to update password"
    );

    return result ?? false;
  };

  const handleContinueAsGuest = async (anonymousEnabled: boolean | null) => {
    if (!anonymousEnabled) {
      navigate("/closed-beta");
      return;
    }

    await executeAuthAction(
      async () => {
        const { error } = await supabase.auth.signInAnonymously();
        if (error) throw error;
        navigate("/");
      },
      "Continuing as guest",
      "Failed to continue as guest"
    ).catch(() => navigate("/closed-beta"));
  };

  const checkAnonymousEnabled = async (): Promise<boolean> => {
    try {
      const { error } = await supabase.auth.signInAnonymously();
      const isEnabled = !error?.message?.includes('Anonymous sign-ins are disabled');
      
      // Clean up test session
      await supabase.auth.signOut();
      
      return isEnabled;
    } catch {
      return false;
    }
  };

  return {
    isLoading,
    isResending,
    needsVerification,
    handleSignUp,
    handleSignUpWithConfirm,
    handleSignIn,
    handleResendVerification,
    handleForgotPassword,
    handleResetPassword,
    handleContinueAsGuest,
    checkAnonymousEnabled,
    validatePassword,
  };
};
